import { type FormEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router";
import { AppNav, storeTrail } from "../../components/AppNav";
import { Button } from "../../components/Button";
import { Input } from "../../components/Input";
import { Layout } from "../../components/Layout";
import { ApiError, api } from "../../lib/api";
import type {
  AlertSort,
  CameraResponse,
  MotionPeriodResponse,
  SensoryLogResponse,
  StoreResponse,
  TimeRange,
  UpdateCameraRequest,
} from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { CameraSurvey } from "./CameraSurvey";
import styles from "./CameraView.module.css";
import { LiveViewPlayer } from "./LiveViewPlayer";
import { SamplingControl } from "./SamplingControl";
import { WatchInstructionsEditor } from "./WatchInstructionsEditor";
import { Highlighted, hiddenMatchLabel, periodSearchText, tokenize } from "./periodSearch";
import { compareByAlert } from "./periodSort";
import {
  observationDisplayText,
  parsePersonRecords,
  peoplePills,
  substituteEntityIds,
} from "./periodText";
import { severityBucket } from "./severity";

const RANGE_MS: Record<TimeRange, number> = {
  "1h": 3_600_000,
  "3h": 10_800_000,
  "6h": 21_600_000,
  "12h": 43_200_000,
  "24h": 86_400_000,
  "2d": 172_800_000,
  "3d": 259_200_000,
  "7d": 604_800_000,
  all: 604_800_000, // placeholder; the real window start comes from the oldest loaded period
};

// A ?period= deep link not found in the current window retries in the next wider one.
const DEEP_LINK_WIDENING: Partial<Record<TimeRange, TimeRange>> = { "24h": "7d", "7d": "all" };

const RANGE_START_LABEL: Record<TimeRange, string> = {
  "1h": "60 min ago",
  "3h": "3h ago",
  "6h": "6h ago",
  "12h": "12h ago",
  "24h": "24h ago",
  "2d": "2 days ago",
  "3d": "3 days ago",
  "7d": "7 days ago",
  all: "first record",
};

const formatClock = (iso: string): string =>
  new Date(iso).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

const pad2 = (n: number): string => String(n).padStart(2, "0");

const meridiem = (d: Date): string => (d.getHours() < 12 ? "AM" : "PM");

// Hours stay zero-padded: 12-hour usually drops the pad, but these sit in table
// columns and a ragged left edge is worse than an unconventional "09".
const hour12 = (d: Date): string => pad2(d.getHours() % 12 || 12);

// Period rows: hh:mm:ss (centiseconds would overlap the category column).
const formatClockSecs2 = (iso: string): string => {
  const d = new Date(iso);
  return `${hour12(d)}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
};

// A range carries the meridiem ONCE when both ends share it ("10:27:48-10:28:45 AM")
// and on both ends only when it straddles noon or midnight — 12-hour clocks would
// otherwise add ~7 characters to every row and push the columns out.
const clockRange = (
  startIso: string,
  endIso: string | null,
  fmt: (iso: string) => string,
): string => {
  const s = new Date(startIso);
  if (endIso === null) return `${fmt(startIso)} ${meridiem(s)}-…`;
  const e = new Date(endIso);
  return meridiem(s) === meridiem(e)
    ? `${fmt(startIso)}-${fmt(endIso)} ${meridiem(e)}`
    : `${fmt(startIso)} ${meridiem(s)}-${fmt(endIso)} ${meridiem(e)}`;
};

// Observations: HH:MM:SS.ss (centiseconds).
const formatClockCs = (iso: string): string => {
  const d = new Date(iso);
  return `${hour12(d)}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}.${pad2(
    Math.floor(d.getMilliseconds() / 10),
  )}`;
};

const formatDay = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });

const formatDayClock = (iso: string): string => `${formatDay(iso)}, ${formatClock(iso)}`;

const formatDayClockSecs = (iso: string): string =>
  `${formatDay(iso)}, ${new Date(iso).toLocaleTimeString("en-US", { hour12: true })}`;

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

const DownloadIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" aria-hidden="true">
    <path d="M5 20h14v-2H5v2zM19 9h-4V3H9v6H5l7 7 7-7z" />
  </svg>
);

// Identifiers are shown truncated (they're UUIDs and the sidebar is narrow) but support
// is always asked for the WHOLE id, so Copy puts the full value on the clipboard.
const IdRow = ({ label, id }: { label: string; id: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className={styles.metaRow}>
      <span>{label}</span>
      <span className={styles.idValue}>
        <span className={styles.metaValue} title={id}>
          {id.slice(0, 8)}
        </span>
        <button
          type="button"
          className={styles.copyUrlBtn}
          aria-label={`Copy full ${label.toLowerCase()} ID`}
          onClick={() => {
            void navigator.clipboard.writeText(id);
            setCopied(true);
            window.setTimeout(() => setCopied(false), 1500);
          }}
        >
          {copied ? "Copied" : "Copy"}
        </button>
      </span>
    </div>
  );
};

const CLIP_POLL_MS = 3_000;
const CLIP_POLL_LIMIT = 90; // ~4.5 min - the clip token's lifetime bound

type VisibleWindow = { start: number; end: number };

// Frames already stream in live; poll the activity window so motion periods and
// alerts follow without a manual reload. A fixed custom range can't grow rows.
const EVENTS_POLL_MS = 30_000;
const SIDEBAR_HIDDEN_KEY = "shobdo.cameraView.sidebarHidden";

// Verbatim from the scale the model is given — shobdo-agent prompts/ssm_v3_period.txt,
// the "severity, rating the scene as a whole" line. These labels had drifted from the
// retired v2 scale: 1 and 2 both read "routine activity" (v3 distinguishes ONE routine
// entity from SEVERAL), and 0 read "no motion" though it also covers environmental-only
// periods, which do have motion (lighting, weather, screens). Keep in step with that
// prompt — the owner-facing meaning of a stored number is set there, not here.
const SEVERITY_MEANINGS: Record<number, string> = {
  0: "empty or environmental only",
  1: "one routine entity",
  2: "several entities, routine",
  3: "noticeable (loitering, concealment, crowding)",
  4: "clearly abnormal (fighting, tampering, forced entry)",
  5: "critical (assault, robbery, fire)",
};

const severityMeaning = (severity: number | null | undefined): string => {
  if (severity == null) return "not rated yet";
  const meaning = SEVERITY_MEANINGS[severity];
  return meaning ? `${severity} — ${meaning}` : String(severity);
};

// How MUCH happened, averaged over the period's chunks (pass-1 activity_level 1–5).
// Severity answers "does it matter"; activity answers "was it busy" — a crowded checkout
// is high activity, low severity; a lone intruder is the reverse.
const ACTIVITY_MEANINGS: Record<number, string> = {
  1: "almost nothing moving",
  2: "a little movement",
  3: "steady activity",
  4: "busy",
  5: "very busy",
};

// "3.7 — steady activity": the number is the period's mean, the words come from the
// nearest whole step on the same 1–5 scale the legend shows.
const activityMeaning = (mean: number | null | undefined): string => {
  if (mean == null) return "not measured yet";
  const step = Math.min(5, Math.max(1, Math.round(mean)));
  return `${mean.toFixed(1)} — ${ACTIVITY_MEANINGS[step]}`;
};

// The legend is one row per scale under the timeline, so it uses short forms. The full
// wording (and, for severity, the prompt's examples) stays on the expanded period row —
// this is a key, not the definition.
const ACTIVITY_LEGEND: Record<number, string> = {
  1: "almost still",
  2: "a little movement",
  3: "steady activity",
  4: "busy",
  5: "very busy",
};

const SEVERITY_LEGEND: Record<number, string> = {
  0: "env. only",
  1: "one entity",
  2: "multiple entities",
  3: "noticeable",
  4: "clearly abnormal",
  5: "critical",
};

// Bar height: mean activity on the 1–5 scale, floored so a real period is never invisible.
// Periods with no chunks yet (activity_mean null) fall back to the floor rather than
// borrowing severity — height and color must stay independent signals.
const MIN_BAR_PCT = 12;
const barHeightPct = (activityMean: number | null | undefined): number => {
  if (activityMean == null) return MIN_BAR_PCT;
  const clamped = Math.min(5, Math.max(1, activityMean));
  return Math.max(MIN_BAR_PCT, Math.round((clamped / 5) * 100));
};

// Left offset (0–100%) of a timestamp inside the visible window.
const positionInWindow = (iso: string, win: VisibleWindow): number => {
  const t = new Date(iso).getTime();
  const span = Math.max(1, win.end - win.start);
  return Math.max(0, Math.min(100, ((t - win.start) / span) * 100));
};

// Ladder of "clean" step sizes. Dense enough that every preset range lands on ~6
// divisions rather than 4 — 24h needs a 4h step, 2d an 8h step, and neither existed
// before, so both fell back to a coarser step and only drew 4 gridlines.
const TICK_STEPS_MS = [
  300_000, // 5m
  600_000, // 10m
  900_000, // 15m
  1_800_000, // 30m
  3_600_000, // 1h
  7_200_000, // 2h
  10_800_000, // 3h
  14_400_000, // 4h
  21_600_000, // 6h
  28_800_000, // 8h
  43_200_000, // 12h
  86_400_000, // 1d
];

// ~6 intermediate ticks aligned to clean local-time boundaries.
const timelineTicks = (win: VisibleWindow): { t: number; label: string }[] => {
  const span = Math.max(1, win.end - win.start);
  const step =
    TICK_STEPS_MS.find((st) => span / st <= 7) ?? Math.ceil(span / 7 / 86_400_000) * 86_400_000;
  const tzOffset = new Date(win.start).getTimezoneOffset() * 60_000;
  const first = (Math.floor((win.start - tzOffset) / step) + 1) * step + tzOffset;
  const ticks: { t: number; label: string }[] = [];
  for (let t = first; t < win.end; t += step) {
    const iso = new Date(t).toISOString();
    ticks.push({
      t,
      label: step >= 86_400_000 ? formatDay(iso) : formatClock(iso),
    });
  }
  return ticks;
};

const formatStamp = (local: string): string => new Date(local).toLocaleString();

type CameraForm = {
  name: string;
  location_label: string;
  description: string;
};

const cameraToForm = (camera: CameraResponse): CameraForm => ({
  name: camera.name,
  location_label: camera.location_label ?? "",
  description: camera.description ?? "",
});

const cameraDiff = (form: CameraForm, camera: CameraResponse): UpdateCameraRequest => {
  const patch: UpdateCameraRequest = {};
  const name = form.name.trim();
  if (name && name !== camera.name) patch.name = name;
  const location = form.location_label.trim();
  if (location !== (camera.location_label ?? "")) {
    patch.location_label = location === "" ? null : location;
  }
  const description = form.description.trim();
  if (description !== (camera.description ?? "")) {
    patch.description = description === "" ? null : description;
  }
  return patch;
};

export const CameraView = () => {
  const { user, logout } = useAuth();
  const { cameraId } = useParams<{ cameraId: string }>();
  const [searchParams] = useSearchParams();
  const linkedPeriodId = searchParams.get("period");

  const [camera, setCamera] = useState<CameraResponse | null>(null);
  const [store, setStore] = useState<StoreResponse | null>(null);
  const [form, setForm] = useState<CameraForm | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [range, setRange] = useState<TimeRange>("24h");
  const [customActive, setCustomActive] = useState(false);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  // "alert" is a pane-local sort (tiers of notified-ness, periodSort.ts); the API
  // keeps its two contract sorts, so alert mode fetches as "time" and orders here.
  const [sort, setSort] = useState<AlertSort | "alert">("severity");
  const [periods, setPeriods] = useState<MotionPeriodResponse[]>([]);
  // The range the rows in `periods` were fetched for (null for a custom window) —
  // read by the deep-link widen so it acts on the rows it asked for, never stale ones.
  const [periodsRange, setPeriodsRange] = useState<TimeRange | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [planLapsed, setPlanLapsed] = useState(false);
  const [expandedPeriodId, setExpandedPeriodId] = useState<string | null>(null);
  const [hoveredPeriodId, setHoveredPeriodId] = useState<string | null>(null);
  const [sidebarHidden, setSidebarHidden] = useState(
    () => localStorage.getItem(SIDEBAR_HIDDEN_KEY) === "1",
  );
  const [periodSensory, setPeriodSensory] = useState<Record<string, SensoryLogResponse[]>>({});
  // Client-side find-as-you-type over the listed periods. The hidden-text corpus
  // (one whole-window sensory fetch + one alerts fetch, grouped by period) loads
  // lazily on the first search and is keyed to the window so a range change
  // invalidates it. Alert decision text is included because owners receive it in
  // their notification and search the words they were sent.
  const [search, setSearch] = useState("");
  const [windowSensory, setWindowSensory] = useState<{
    sig: string;
    rows: Record<string, SensoryLogResponse[]>;
    alerts: Record<string, string>;
  } | null>(null);
  const [windowSensoryLoading, setWindowSensoryLoading] = useState(false);
  // Period ids observed in THIS view since the last fetch. The dot clears from here;
  // `p.unread` stays as fetched so the unread-first sort can't yank an open row away
  // from under the cursor (order re-settles on the next fetch).
  const [locallyObserved, setLocallyObserved] = useState<Set<string>>(new Set());
  const [pinnedRow, setPinnedRow] = useState<MotionPeriodResponse | null>(null);
  const [now, setNow] = useState(() => Date.now());

  type ClipFlow = {
    periodId: string;
    mode: "play" | "download";
    phase: "confirm" | "password" | "working" | "error";
    message?: string;
  };
  const [clipFlow, setClipFlow] = useState<ClipFlow | null>(null);
  const [clipPassword, setClipPassword] = useState("");
  const [clipOverlay, setClipOverlay] = useState<{
    src: string;
    range: string;
  } | null>(null);
  const [copiedPeriodId, setCopiedPeriodId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!cameraId) return;
    try {
      const cam = await api.getCamera(cameraId);
      setCamera(cam);
      setForm(cameraToForm(cam));
      setStore(await api.getStore(cam.store_id));
    } catch (e) {
      setError(e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't load this camera.");
    } finally {
      setLoading(false);
    }
  }, [cameraId]);

  const customWindow = customActive && customFrom && customTo;

  const loadEvents = useCallback(async () => {
    if (!cameraId) return;
    setEventsError(null);
    try {
      const win = customWindow
        ? {
            from: new Date(customFrom).toISOString(),
            to: new Date(customTo).toISOString(),
          }
        : { range };
      setPeriods(
        await api.listCameraPeriods(cameraId, {
          ...win,
          sort: sort === "alert" ? "time" : sort,
        }),
      );
      // Which range these rows were fetched for. The deep-link widen must not conclude
      // "not in 7d either" while the rows in state are still the 24h fetch.
      setPeriodsRange(customWindow ? null : range);
      setLocallyObserved(new Set());
      setPlanLapsed(false);
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setPlanLapsed(true);
        return;
      }
      setEventsError(
        e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't load activity.",
      );
    }
  }, [cameraId, range, sort, customWindow, customFrom, customTo]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    void loadEvents();
  }, [loadEvents]);

  useEffect(() => {
    if (customWindow || planLapsed) return;
    const id = window.setInterval(() => {
      if (document.visibilityState === "visible") void loadEvents();
    }, EVENTS_POLL_MS);
    return () => window.clearInterval(id);
  }, [customWindow, planLapsed, loadEvents]);

  // Lazy clip-text corpus: nothing fetches at page load; the first keystroke pulls
  // the whole selected window's sensory rows once (same opts as loadEvents) and the
  // search widens from descriptions-only to clip text when it lands. Best-effort —
  // a failed fetch leaves description search working.
  const corpusSig = customWindow ? `${cameraId}|${customFrom}|${customTo}` : `${cameraId}|${range}`;
  useEffect(() => {
    if (!cameraId || tokenize(search).length === 0) return;
    if (windowSensoryLoading || windowSensory?.sig === corpusSig) return;
    // No cancellation: the result is written under the sig it was fetched for, and a
    // reader that has moved to another window simply ignores it (sig mismatch).
    const sig = corpusSig;
    const win = customWindow
      ? {
          from: new Date(customFrom).toISOString(),
          to: new Date(customTo).toISOString(),
        }
      : { range };
    setWindowSensoryLoading(true);
    // allSettled: either half failing leaves the other searchable (best-effort).
    void Promise.allSettled([
      api.listCameraSensory(cameraId, win),
      api.listCameraAlerts(cameraId, win),
    ])
      .then(([sensoryRes, alertsRes]) => {
        const grouped: Record<string, SensoryLogResponse[]> = {};
        if (sensoryRes.status === "fulfilled") {
          for (const r of sensoryRes.value) {
            grouped[r.period_id] ??= [];
            grouped[r.period_id]?.push(r);
          }
        }
        const alerts: Record<string, string> = {};
        if (alertsRes.status === "fulfilled") {
          for (const a of alertsRes.value) {
            alerts[a.period_id] = [a.cause, a.reason].filter(Boolean).join("\n");
          }
        }
        setWindowSensory({ sig, rows: grouped, alerts });
      })
      .finally(() => setWindowSensoryLoading(false));
  }, [
    cameraId,
    search,
    corpusSig,
    windowSensory,
    windowSensoryLoading,
    customWindow,
    customFrom,
    customTo,
    range,
  ]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const sortedPeriods = useMemo(() => {
    const arr = [...periods];
    // Pin the open row: a refresh may no longer return it (Play's server-side observe
    // mark ends its range escape; the range window slides; another member marks it) —
    // without the pin that reads as the row "closing itself" mid-view. Released on close.
    if (
      pinnedRow &&
      expandedPeriodId === pinnedRow.period_id &&
      !arr.some((r) => r.period_id === pinnedRow.period_id)
    )
      arr.push(pinnedRow);
    if (sort === "alert") return arr.sort(compareByAlert);
    if (sort === "severity") {
      // Mirror the API's severity sort exactly (ADR 0005, 2026-07-31): unread first,
      // then severity, then recency. Uses `p.unread` as fetched — the local dot-clear
      // lives in `locallyObserved` — so opening a row never reorders the list.
      const unread = (p: MotionPeriodResponse): number => (p.unread ? 1 : 0);
      return arr.sort(
        (a, b) =>
          unread(b) - unread(a) ||
          (b.severity ?? -1) - (a.severity ?? -1) ||
          new Date(b.period_start_ts).getTime() - new Date(a.period_start_ts).getTime(),
      );
    }
    return arr.sort(
      (a, b) => new Date(b.period_start_ts).getTime() - new Date(a.period_start_ts).getTime(),
    );
  }, [periods, sort, pinnedRow, expandedPeriodId]);

  const searchTokens = useMemo(() => tokenize(search), [search]);
  const corpus = windowSensory?.sig === corpusSig ? windowSensory : null;
  // The searchable text per period, prebuilt and lowercased ONCE per data change.
  // Building it per keystroke re-parsed entity records across the whole window's
  // sensory rows (~14k at 7d) on every character — the measured 7d typing lag.
  const searchTexts = useMemo(() => {
    const map = new Map<string, string>();
    // sortedPeriods, not periods: the pinned row can outlive the fetch that returned
    // it, and a row the pane renders must stay searchable.
    for (const p of sortedPeriods) {
      const obs = corpus?.rows[p.period_id] ?? periodSensory[p.period_id];
      map.set(p.period_id, periodSearchText(p, obs, corpus?.alerts[p.period_id]).toLowerCase());
    }
    return map;
  }, [sortedPeriods, corpus, periodSensory]);
  const filteredPeriods = useMemo(() => {
    if (searchTokens.length === 0) return sortedPeriods;
    return sortedPeriods.filter((p) => {
      const text = searchTexts.get(p.period_id) ?? "";
      return searchTokens.every((t) => text.includes(t));
    });
  }, [sortedPeriods, searchTokens, searchTexts]);

  const oldestPeriodMs = useMemo(
    () =>
      periods.length
        ? Math.min(...periods.map((p) => new Date(p.period_start_ts).getTime()))
        : null,
    [periods],
  );

  const win: VisibleWindow = customWindow
    ? {
        start: new Date(customFrom).getTime(),
        end: new Date(customTo).getTime(),
      }
    : range === "all"
      ? { start: oldestPeriodMs ?? now - RANGE_MS["7d"], end: now }
      : { start: now - RANGE_MS[range], end: now };

  const hoveredPeriod = hoveredPeriodId
    ? (periods.find((p) => p.period_id === hoveredPeriodId) ?? null)
    : null;
  // A window that crosses a local midnight renders the day on every row —
  // clock times alone would mix days indistinguishably.
  const multiDay = new Date(win.start).toDateString() !== new Date(win.end).toDateString();

  // Scroll only the period LIST (never the page) so a period's row sits near the top.
  const scrollListToPeriod = useCallback((periodId: string): void => {
    const container = document.querySelector("[data-period-scroll]");
    const row = document.querySelector(`[data-period-row="${periodId}"]`);
    if (container instanceof HTMLElement && row instanceof HTMLElement) {
      container.scrollTop +=
        row.getBoundingClientRect().top - container.getBoundingClientRect().top - 8;
    }
  }, []);

  // Deep link: /cameras/<id>?period=<period_id> opens with that period expanded.
  const [deepLinkDone, setDeepLinkDone] = useState(false);
  useEffect(() => {
    if (!linkedPeriodId || deepLinkDone) return;
    const hit = periods.find((p) => p.period_id === linkedPeriodId);
    if (hit) {
      setDeepLinkDone(true);
      setExpandedPeriodId(linkedPeriodId);
      // Layout above the pane (live player, timeline) settles over several frames, so a
      // single early scroll aligns against a half-built page. Re-assert until the pane
      // position is stable for two consecutive attempts (or ~1.5s).
      const align = (): void => {
        const pane = document.querySelector("[data-periods-pane]");
        const container = document.querySelector("[data-period-scroll]");
        const row = document.querySelector(`[data-period-row="${linkedPeriodId}"]`);
        if (!(pane instanceof HTMLElement)) return;
        pane.scrollIntoView?.({ block: "start" }); // best-effort; absent in jsdom
        if (container instanceof HTMLElement && row instanceof HTMLElement) {
          container.scrollTop +=
            row.getBoundingClientRect().top - container.getBoundingClientRect().top - 8;
        }
      };
      let attempts = 0;
      let lastTop = Number.NaN;
      const tick = (): void => {
        attempts += 1;
        align();
        const pane = document.querySelector("[data-periods-pane]");
        const top = pane instanceof HTMLElement ? pane.getBoundingClientRect().top : Number.NaN;
        const stable = Math.abs(top - lastTop) < 1;
        lastTop = top;
        if (!stable && attempts < 12) window.setTimeout(tick, 120);
      };
      window.setTimeout(tick, 60);
      return;
    }
    // The period is not in the rows we HAVE. Only conclude anything once the rows in
    // state are the ones fetched for the current range — setRange re-fires this effect
    // immediately with the previous fetch still in `periods`, and concluding from
    // those stale rows is what silently gave up on every >24h deep link (the widen
    // defeated itself).
    if (periodsRange !== range) return;
    // Widen 24h → 7d → all: store-report links can point at any age (issue #8).
    const wider = DEEP_LINK_WIDENING[range];
    if (wider) setRange(wider);
    else setDeepLinkDone(true);
  }, [linkedPeriodId, deepLinkDone, periods, periodsRange, range]);

  // The observation is recorded when the row is CLOSED, not opened (operator ruling
  // 2026-08-01, ADR 0005): marking on open let the background refresh drop an
  // out-of-range unread row from the escaped view while it was still being read.
  const commitObserved = useCallback(
    (periodId: string) => {
      if (!cameraId) return;
      void api.markPeriodObserved(cameraId, periodId).catch(() => {});
      setLocallyObserved((prev) => new Set(prev).add(periodId));
    },
    [cameraId],
  );

  // Opened-but-never-collapsed still counts as seen: commit the open unread row on
  // unmount (navigation away). Ref, not state — the cleanup must see the latest value.
  const openUnreadRef = useRef<string | null>(null);
  useEffect(
    () => () => {
      if (openUnreadRef.current) commitObserved(openUnreadRef.current);
    },
    [commitObserved],
  );

  const togglePeriod = async (p: MotionPeriodResponse): Promise<void> => {
    if (expandedPeriodId === p.period_id) {
      setExpandedPeriodId(null);
      setPinnedRow(null);
      openUnreadRef.current = null;
      if (p.unread && !locallyObserved.has(p.period_id)) commitObserved(p.period_id);
      return;
    }
    if (expandedPeriodId !== null) {
      // Switching rows implicitly closes the previous one.
      const prev = sortedPeriods.find((row) => row.period_id === expandedPeriodId);
      if (prev?.unread && !locallyObserved.has(prev.period_id)) commitObserved(prev.period_id);
    }
    setExpandedPeriodId(p.period_id);
    setPinnedRow(p);
    openUnreadRef.current = p.unread && !locallyObserved.has(p.period_id) ? p.period_id : null;
    if (!periodSensory[p.period_id] && cameraId) {
      try {
        const rows = await api.listCameraSensory(cameraId, {
          from: p.period_start_ts,
          to: p.period_end_ts,
          periodId: p.period_id,
        });
        setPeriodSensory((prev) => ({ ...prev, [p.period_id]: rows }));
      } catch {
        // The row stays expandable with verdict data even if observations fail to load.
      }
    }
  };

  const finishClip = (p: MotionPeriodResponse, downloadUrl: string, mode: "play" | "download") => {
    setClipFlow(null);
    if (mode === "download") {
      const a = document.createElement("a");
      a.href = `${downloadUrl}&dl=1`;
      a.download = "";
      a.click();
      return;
    }
    setClipOverlay({
      src: downloadUrl,
      range: `${formatDayClockSecs(p.period_start_ts)} \u2013 ${formatDayClockSecs(p.period_end_ts)}`,
    });
  };

  const submitClipPassword = async (p: MotionPeriodResponse): Promise<void> => {
    if (!cameraId || !clipFlow) return;
    const mode = clipFlow.mode;
    const password = clipPassword;
    setClipPassword("");
    setClipFlow({
      periodId: p.period_id,
      mode,
      phase: "working",
      message: "Requesting clip…",
    });
    try {
      const tok = await api.createClipToken(cameraId, p.period_id, password);
      // Play uses the 3 fps preview artifact (ready in ~a minute); Download the full clip.
      const variant = mode === "play" ? "&variant=preview" : "";
      const first = await fetch(`${tok.request_url}${variant}`, {
        method: "POST",
      });
      const firstBody = await first.json().catch(() => null);
      if (!first.ok) {
        throw new Error(firstBody?.detail ?? `request failed (${first.status})`);
      }
      if (firstBody.status === "ready") {
        finishClip(p, `${tok.download_url}${variant}`, mode);
        return;
      }
      setClipFlow({
        periodId: p.period_id,
        mode,
        phase: "working",
        message: "Preparing clip… this can take a few minutes for long periods.",
      });
      const statusUrl =
        firstBody.task_id != null
          ? `${tok.status_url}${variant}&task_id=${firstBody.task_id}`
          : `${tok.status_url}${variant}`;
      for (let i = 0; i < CLIP_POLL_LIMIT; i += 1) {
        await new Promise((r) => setTimeout(r, CLIP_POLL_MS));
        const poll = await fetch(statusUrl);
        const body = await poll.json().catch(() => null);
        if (!poll.ok) throw new Error(body?.detail ?? `status failed (${poll.status})`);
        if (body.status === "ready") {
          finishClip(p, `${tok.download_url}${variant}`, mode);
          return;
        }
        if (body.status === "failed") {
          throw new Error(`clip fetch failed on the recorder (${body.ota_status ?? "error"})`);
        }
      }
      throw new Error("Still preparing — click again in a few minutes; it resumes from the cache.");
    } catch (e) {
      const message = e instanceof ApiError ? (e.body?.error ?? e.message) : (e as Error).message;
      setClipFlow({ periodId: p.period_id, mode, phase: "error", message });
    }
  };

  if (!cameraId) return <Navigate to="/stores" replace />;

  const updateForm = (key: keyof CameraForm, value: string): void =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const handleSave = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    if (!camera || !form) return;
    const patch = cameraDiff(form, camera);
    if (Object.keys(patch).length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateCamera(camera.id, patch);
      setCamera(updated);
      setForm(cameraToForm(updated));
    } catch (e) {
      setError(
        e instanceof ApiError ? (e.body?.error ?? e.message) : "Couldn't update the camera.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDiscard = (): void => {
    if (camera) setForm(cameraToForm(camera));
  };

  const dirty = camera && form ? Object.keys(cameraDiff(form, camera)).length > 0 : false;
  // Desired-vs-actual (ADR 0017): a pending request renders as in-transition, never as done.
  const pendingDesired =
    camera &&
    camera.desired_enabled !== null &&
    camera.desired_enabled !== camera.is_enabled &&
    (camera.desired_status === "pending" || camera.desired_status === "enqueued");
  const stateLabel = !camera
    ? null
    : pendingDesired
      ? {
          label: camera.desired_enabled ? "Enabling…" : "Disabling…",
          state: "pending",
        }
      : !camera.is_enabled
        ? { label: "Disabled", state: "disabled" }
        : { label: "Enabled", state: "live" };
  useEffect(() => {
    if (!clipOverlay) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setClipOverlay(null);
    };
    document.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [clipOverlay]);

  return (
    <Layout
      nav={
        <AppNav
          trail={storeTrail(store, camera?.name ?? (loading ? "Loading" : "Camera"))}
          onSignOut={logout}
          userName={user?.display_name ?? user?.email}
          userAvatar={user?.avatar ?? null}
        />
      }
    >
      <main>
        <div className={styles.header}>
          <Link to={store ? `/stores/${store.id}?tab=cameras` : "/stores"} className={styles.crumb}>
            ← {store?.name ?? "Back"}
          </Link>
          <h1 className={styles.title}>
            {camera?.name ?? (loading ? "Loading…" : "Camera not found")}
          </h1>
          {stateLabel && (
            <span className={styles.stateTag} data-state={stateLabel.state}>
              <span className={styles.stateDot} aria-hidden="true" />
              {stateLabel.label}
            </span>
          )}
          <button
            type="button"
            className={styles.sidebarToggle}
            aria-label="Toggle details sidebar"
            aria-expanded={!sidebarHidden}
            onClick={() =>
              setSidebarHidden((h) => {
                const next = !h;
                localStorage.setItem(SIDEBAR_HIDDEN_KEY, next ? "1" : "0");
                return next;
              })
            }
          >
            <span aria-hidden="true">☰</span>
          </button>
        </div>

        {loading ? (
          <div className={styles.status}>Loading…</div>
        ) : !camera || !form ? (
          <div className={styles.status}>{error ?? "Camera not found."}</div>
        ) : (
          <div className={styles.body} data-sidebar-hidden={sidebarHidden}>
            <div className={styles.videoWrap}>
              {planLapsed ? (
                <div className={styles.status} role="alert">
                  This camera has no active plan. Live view and activity are disabled — renew the
                  plan to turn them back on.
                </div>
              ) : (
                <>
                  <LiveViewPlayer cameraId={camera.id} cameraName={camera.name} />

                  <div className={styles.timeline}>
                    <div className={styles.timelineHeader}>
                      <span>
                        {hoveredPeriod
                          ? `${formatDayClock(hoveredPeriod.period_start_ts)} \u2013 ${formatDayClock(hoveredPeriod.period_end_ts)}`
                          : "Timeline"}
                      </span>
                      <div className={styles.timelineRange}>
                        {(["1h", "3h", "6h", "12h", "24h", "2d", "3d", "7d", "all"] as const).map(
                          (r) => (
                            <button
                              key={r}
                              type="button"
                              className={styles.rangeBtn}
                              data-active={!customActive && range === r}
                              onClick={() => {
                                setCustomActive(false);
                                setRange(r);
                              }}
                            >
                              {r === "all" ? "All" : r}
                            </button>
                          ),
                        )}
                        <button
                          type="button"
                          className={styles.rangeBtn}
                          data-active={customActive}
                          onClick={() => setCustomActive(true)}
                        >
                          custom
                        </button>
                      </div>
                    </div>
                    {customActive && (
                      <div className={styles.customRange}>
                        <input
                          type="datetime-local"
                          aria-label="From"
                          value={customFrom}
                          onChange={(e) => setCustomFrom(e.target.value)}
                        />
                        <input
                          type="datetime-local"
                          aria-label="To"
                          value={customTo}
                          onChange={(e) => setCustomTo(e.target.value)}
                        />
                      </div>
                    )}
                    <div className={styles.timelineBar}>
                      {timelineTicks(win).map((tick) => (
                        <span
                          key={tick.t}
                          className={styles.tick}
                          style={{
                            left: `${((tick.t - win.start) / Math.max(1, win.end - win.start)) * 100}%`,
                          }}
                          aria-hidden="true"
                        />
                      ))}
                      {periods
                        // Unread rows escape the range filter server-side (ADR 0005);
                        // they belong in the LIST but not on this window's axis — a
                        // clamped bar at 0% would misread as activity at the window edge.
                        .filter(
                          (p) => !(p.unread && new Date(p.period_start_ts).getTime() < win.start),
                        )
                        .map((p) => (
                          <button
                            key={p.period_id}
                            type="button"
                            className={styles.activityBar}
                            data-severity={severityBucket(p.severity ?? 0)}
                            data-active={expandedPeriodId === p.period_id}
                            data-hovered={hoveredPeriodId === p.period_id}
                            style={{
                              left: `${positionInWindow(p.period_start_ts, win)}%`,
                              height: `${barHeightPct(p.activity_mean)}%`,
                            }}
                            aria-label={`Motion period ${formatClock(p.period_start_ts)}–${formatClock(p.period_end_ts)}, severity ${severityMeaning(p.severity)}, activity ${p.activity_mean == null ? "not measured yet" : p.activity_mean.toFixed(1)} of 5`}
                            title={`${formatDayClock(p.period_start_ts)} \u2013 ${formatDayClock(p.period_end_ts)}`}
                            onMouseEnter={() => setHoveredPeriodId(p.period_id)}
                            onMouseLeave={() => setHoveredPeriodId(null)}
                            onFocus={() => setHoveredPeriodId(p.period_id)}
                            onBlur={() => setHoveredPeriodId(null)}
                            onClick={() => {
                              void togglePeriod(p);
                              // After the re-render (another period may collapse above),
                              // bring this period's row into the list view. List only —
                              // the page must not move.
                              window.setTimeout(() => scrollListToPeriod(p.period_id), 60);
                            }}
                          />
                        ))}
                      {/* Periods only: alert + sent already live in the table
                          rows; duplicate markers made the bar read as two
                          different kinds of activity. */}
                      {periods.length === 0 && (
                        <span className={styles.timelineEmpty}>No activity in this range.</span>
                      )}
                    </div>
                    {/* Tick text sits BELOW the track, not inside it: over the bars it
                        collided with tall periods and read as data. */}
                    <div className={styles.tickLabels} aria-hidden="true">
                      {timelineTicks(win).map((tick) => (
                        <span
                          key={tick.t}
                          className={styles.tickLabel}
                          style={{
                            left: `${((tick.t - win.start) / Math.max(1, win.end - win.start)) * 100}%`,
                          }}
                        >
                          {tick.label}
                        </span>
                      ))}
                    </div>
                    <div className={styles.timelineLabels}>
                      <span>
                        {customWindow ? formatStamp(customFrom) : RANGE_START_LABEL[range]}
                      </span>
                      <span>{customWindow ? formatStamp(customTo) : "NOW"}</span>
                    </div>
                    {/* Two independent signals share one bar, so say which is which:
                        taller = busier (activity 1-5), warmer = more serious (severity
                        0-5). One grid for both rows, so each severity point sits under
                        its activity counterpart. */}
                    <div className={styles.timelineLegend} aria-label="Timeline legend">
                      <span className={styles.legendTitle}>Bar height — activity</span>
                      {/* Activity starts at 1; the 0 column stays empty so every number
                          sits above its severity counterpart. Narrow layouts drop the
                          alignment grid entirely, so this spacer is hidden there. */}
                      <span className={styles.legendSpacer} aria-hidden="true" />
                      {[1, 2, 3, 4, 5].map((level) => (
                        <span key={`a${level}`} className={styles.legendItem}>
                          <span className={styles.legendBarBox} aria-hidden="true">
                            <span
                              className={styles.legendBar}
                              style={{ height: `${barHeightPct(level)}%` }}
                            />
                          </span>
                          <span>
                            {level} — {ACTIVITY_LEGEND[level]}
                          </span>
                        </span>
                      ))}
                      <span className={styles.legendTitle}>Bar color — severity</span>
                      {[0, 1, 2, 3, 4, 5].map((level) => (
                        <span key={`s${level}`} className={styles.legendItem}>
                          <span
                            className={styles.legendSwatch}
                            data-severity={severityBucket(level)}
                            aria-hidden="true"
                          />
                          <span>
                            {level} — {SEVERITY_LEGEND[level]}
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>

                  <section
                    className={styles.eventPanel}
                    aria-label="Alert decisions"
                    data-periods-pane
                  >
                    <div className={styles.eventHeader}>
                      <span className={styles.panelKicker}>Motion periods</span>
                      <input
                        type="search"
                        className={styles.periodSearch}
                        aria-label="Search motion periods"
                        title='All words must match; use "quotes" for an exact phrase'
                        placeholder="Search descriptions…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      {searchTokens.length > 0 && windowSensoryLoading && (
                        <span className={styles.searchHint}>loading clip text…</span>
                      )}
                      <div className={styles.timelineRange}>
                        {(["severity", "time", "alert"] as const).map((mode) => (
                          <button
                            key={mode}
                            type="button"
                            className={styles.rangeBtn}
                            data-active={sort === mode}
                            title={
                              mode === "alert"
                                ? "Notified first, then undecided alerts, then suppressed"
                                : undefined
                            }
                            onClick={() => setSort(mode)}
                          >
                            {mode}
                          </button>
                        ))}
                      </div>
                    </div>

                    {eventsError && (
                      <div role="alert" className={styles.error}>
                        {eventsError}
                      </div>
                    )}

                    <div className={styles.periodScroll} data-period-scroll>
                      <table className={styles.periodTable} aria-label="Motion period list">
                        <thead>
                          <tr>
                            <th className={styles.colSev}>Sev</th>
                            <th className={styles.colTime}>Start–End</th>
                            <th className={styles.colCat}>Category</th>
                            <th>Description</th>
                            <th className={styles.colFlag}>Alert</th>
                            <th className={styles.colFlag}>Sent</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPeriods.length === 0 ? (
                            <tr>
                              <td colSpan={6} className={styles.empty}>
                                {searchTokens.length > 0 && sortedPeriods.length > 0
                                  ? `No periods match "${search.trim()}".`
                                  : "No motion periods in this range."}
                              </td>
                            </tr>
                          ) : (
                            filteredPeriods.map((p) => {
                              const bucket = severityBucket(p.severity ?? 0);
                              const expanded = expandedPeriodId === p.period_id;
                              const observations = periodSensory[p.period_id];
                              return (
                                <Fragment key={p.period_id}>
                                  <tr
                                    className={styles.periodRow}
                                    data-period-row={p.period_id}
                                    data-active={expanded}
                                    data-hovered={hoveredPeriodId === p.period_id}
                                    aria-expanded={expanded}
                                    tabIndex={0}
                                    onClick={() => void togglePeriod(p)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        void togglePeriod(p);
                                      }
                                    }}
                                  >
                                    <td>
                                      {p.unread && !locallyObserved.has(p.period_id) && (
                                        <span
                                          className={styles.unreadDot}
                                          aria-label="unread alert"
                                        />
                                      )}
                                      <span className={styles.severity} data-severity={bucket}>
                                        {p.severity ?? "–"}
                                      </span>
                                    </td>
                                    <td className={styles.periodTime}>
                                      {multiDay && (
                                        <span className={styles.periodDay}>
                                          {formatDay(p.period_start_ts)}
                                        </span>
                                      )}
                                      {clockRange(
                                        p.period_start_ts,
                                        p.closed ? p.period_end_ts : null,
                                        formatClockSecs2,
                                      )}
                                    </td>
                                    <td className={styles.periodCell}>{p.category ?? "–"}</td>
                                    <td className={styles.periodDesc}>
                                      {/* Inner span: a phone clamps it to two lines, which a
                                          table cell itself cannot do. */}
                                      <span className={styles.periodDescText}>
                                        {/* SSM v3: nothing is written until close — an open
                                            period renders as in-progress, not empty. */}
                                        {p.closed ? (
                                          <Highlighted text={p.description ?? "–"} query={search} />
                                        ) : (
                                          <Highlighted
                                            text={p.description ?? "⏺ motion in progress"}
                                            query={search}
                                          />
                                        )}
                                        {(() => {
                                          const label = hiddenMatchLabel(
                                            p,
                                            corpus?.alerts[p.period_id],
                                            searchTokens,
                                          );
                                          return label === null ? null : (
                                            <span
                                              className={styles.searchChip}
                                              title="Matched in text this row doesn't show"
                                            >
                                              {label}
                                            </span>
                                          );
                                        })()}
                                      </span>
                                    </td>
                                    <td className={styles.periodFlag}>{p.alert ? "Y" : "N"}</td>
                                    <td className={styles.periodFlag}>
                                      {p.decision === "convey"
                                        ? "Y"
                                        : p.decision === "suppress"
                                          ? "N"
                                          : "–"}
                                    </td>
                                  </tr>
                                  {expanded && (
                                    <tr className={styles.periodDetailRow}>
                                      <td colSpan={6}>
                                        <div className={styles.periodDetail}>
                                          <div>
                                            <span className={styles.detailLabel}>Description</span>
                                            <Highlighted
                                              text={
                                                p.description
                                                  ? substituteEntityIds(
                                                      p.description,
                                                      observations
                                                        ? parsePersonRecords(observations)
                                                        : [],
                                                    )
                                                  : "No description yet."
                                              }
                                              query={search}
                                            />
                                          </div>
                                          {observations !== undefined &&
                                            parsePersonRecords(observations).length > 0 && (
                                              <div>
                                                <span className={styles.detailLabel}>People</span>
                                                <div
                                                  aria-label="People in this period"
                                                  className={styles.peopleRow}
                                                >
                                                  {peoplePills(
                                                    parsePersonRecords(observations),
                                                  ).map((text) => (
                                                    <span key={text} className={styles.personChip}>
                                                      <Highlighted text={text} query={search} />
                                                    </span>
                                                  ))}
                                                </div>
                                              </div>
                                            )}
                                          <div>
                                            <span className={styles.detailLabel}>Reason</span>
                                            <Highlighted
                                              text={p.reason ?? "No reason recorded."}
                                              query={search}
                                            />
                                          </div>
                                          {p.decision === "convey" && (
                                            <div>
                                              <span className={styles.detailLabel}>Dispatched</span>
                                              {p.channels_notified.length > 0
                                                ? p.channels_notified.join(", ")
                                                : "no channels"}
                                            </div>
                                          )}
                                          <div>
                                            <span className={styles.detailLabel}>Activity</span>
                                            {activityMeaning(p.activity_mean)}
                                          </div>
                                          <div>
                                            <span className={styles.detailLabel}>Severity</span>
                                            {severityMeaning(p.severity)}
                                          </div>
                                          <div className={styles.periodIdRow}>
                                            <div>
                                              <span className={styles.detailLabel}>Period ID</span>
                                              <code className={styles.periodId}>{p.period_id}</code>
                                              <button
                                                type="button"
                                                className={styles.copyUrlBtn}
                                                title="Copy URL"
                                                aria-label="Copy period URL"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  void navigator.clipboard.writeText(
                                                    `${window.location.origin}/cameras/${cameraId}?period=${p.period_id}`,
                                                  );
                                                  setCopiedPeriodId(p.period_id);
                                                  window.setTimeout(
                                                    () => setCopiedPeriodId(null),
                                                    1500,
                                                  );
                                                }}
                                              >
                                                {copiedPeriodId === p.period_id
                                                  ? "Copied"
                                                  : "Copy URL"}
                                              </button>
                                            </div>
                                            {/* Playback exists only for closed periods
                                                (the clip plane 409s open ones): no
                                                buttons, no doomed password prompt. */}
                                            {p.closed && (
                                              <div className={styles.clipActions}>
                                                <button
                                                  type="button"
                                                  className={styles.clipBtn}
                                                  aria-label="Play period clip"
                                                  title="Play the recorded clip"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setClipPassword("");
                                                    setClipFlow({
                                                      periodId: p.period_id,
                                                      mode: "play",
                                                      phase: "password",
                                                    });
                                                  }}
                                                >
                                                  <PlayIcon />
                                                </button>
                                                <button
                                                  type="button"
                                                  className={styles.clipBtn}
                                                  aria-label="Download period clip"
                                                  title="Download the recorded clip"
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    setClipPassword("");
                                                    setClipFlow({
                                                      periodId: p.period_id,
                                                      mode: "download",
                                                      phase: "confirm",
                                                    });
                                                  }}
                                                >
                                                  <DownloadIcon />
                                                </button>
                                              </div>
                                            )}
                                          </div>
                                          {clipFlow?.periodId === p.period_id && (
                                            <div className={styles.clipFlow}>
                                              {clipFlow.phase === "confirm" && (
                                                <div className={styles.clipConfirm}>
                                                  <span>
                                                    Downloading prepares the full clip on the store
                                                    recorder — this can take several minutes for
                                                    long periods.
                                                  </span>
                                                  <Button
                                                    size="sm"
                                                    onClick={() =>
                                                      setClipFlow({
                                                        periodId: p.period_id,
                                                        mode: "download",
                                                        phase: "password",
                                                      })
                                                    }
                                                  >
                                                    Continue
                                                  </Button>
                                                  <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setClipFlow(null)}
                                                  >
                                                    Cancel
                                                  </Button>
                                                </div>
                                              )}
                                              {clipFlow.phase === "password" && (
                                                <form
                                                  className={styles.clipPasswordForm}
                                                  onSubmit={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    void submitClipPassword(p);
                                                  }}
                                                  onClick={(e) => e.stopPropagation()}
                                                  onKeyDown={(e) => e.stopPropagation()}
                                                >
                                                  <input
                                                    type="password"
                                                    aria-label="Account password"
                                                    placeholder="Account password"
                                                    value={clipPassword}
                                                    onChange={(e) =>
                                                      setClipPassword(e.target.value)
                                                    }
                                                  />
                                                  <Button type="submit" size="sm">
                                                    Confirm
                                                  </Button>
                                                  <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={() => setClipFlow(null)}
                                                  >
                                                    Cancel
                                                  </Button>
                                                </form>
                                              )}
                                              {clipFlow.phase === "working" && (
                                                <span className={styles.clipStatus}>
                                                  {clipFlow.message}
                                                </span>
                                              )}
                                              {clipFlow.phase === "error" && (
                                                <span role="alert" className={styles.clipError}>
                                                  {clipFlow.message}
                                                </span>
                                              )}
                                            </div>
                                          )}
                                          <span className={styles.detailLabel}>Observations</span>
                                          {observations === undefined ? (
                                            <div>Loading observations…</div>
                                          ) : observations.length === 0 ? (
                                            <div>No sensory logs in this period.</div>
                                          ) : (
                                            <div aria-label="Sensory observations">
                                              {observations.map((o) => (
                                                <div key={o.id} className={styles.observationLine}>
                                                  <span className={styles.observationMeta}>
                                                    {clockRange(
                                                      o.clip_start_ts,
                                                      o.clip_end_ts,
                                                      formatClockCs,
                                                    )}
                                                  </span>
                                                  <span className={styles.observationMeta}>
                                                    activity {o.activity_level}
                                                  </span>
                                                  <span className={styles.observationText}>
                                                    <Highlighted
                                                      text={observationDisplayText(o)}
                                                      query={search}
                                                    />
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </Fragment>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </section>
                </>
              )}
            </div>

            {sidebarHidden ? null : (
              <aside className={styles.sidebar} aria-label="Details sidebar">
                <form className={styles.panel} onSubmit={handleSave} noValidate>
                  <div className={styles.panelHeader}>
                    <span className={styles.panelKicker}>Camera</span>
                    <div className={styles.panelActions}>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleDiscard}
                        disabled={!dirty || saving}
                      >
                        Discard
                      </Button>
                      <Button type="submit" size="sm" disabled={!dirty || saving}>
                        {saving ? "Saving…" : "Save"}
                      </Button>
                    </div>
                  </div>

                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="camera-name">
                      Name
                    </label>
                    <Input
                      id="camera-name"
                      value={form.name}
                      onChange={(e) => updateForm("name", e.target.value)}
                      disabled={saving}
                      required
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="camera-location">
                      Location
                    </label>
                    <Input
                      id="camera-location"
                      value={form.location_label}
                      onChange={(e) => updateForm("location_label", e.target.value)}
                      disabled={saving}
                    />
                  </div>
                  <div className={styles.field}>
                    <label className={styles.label} htmlFor="camera-description">
                      Description
                    </label>
                    <textarea
                      id="camera-description"
                      className={styles.textarea}
                      value={form.description}
                      onChange={(e) => updateForm("description", e.target.value)}
                      disabled={saving}
                      rows={5}
                    />
                  </div>
                  {error && (
                    <div role="alert" className={styles.error}>
                      {error}
                    </div>
                  )}
                  <SamplingControl camera={camera} onChanged={(cam) => setCamera(cam)} />
                </form>

                <WatchInstructionsEditor scope={{ kind: "camera", id: camera.id }} />

                <CameraSurvey camera={camera} onChanged={() => void reload()} />

                <div className={styles.panel} aria-label="Technical">
                  <span className={styles.panelKicker}>Technical</span>
                  <IdRow label="Store" id={camera.store_id} />
                  <IdRow label="Camera" id={camera.id} />
                  {/* Read-only since ADR 0017 (incident 0004): the edge box owns its
                      camera roster and binds; what's shown mirrors the edge's report. */}
                  {camera.edge_agent_id ? (
                    <>
                      <IdRow label="Edge agent" id={camera.edge_agent_id} />
                      <div className={styles.metaRow}>
                        <span>Asset ID</span>
                        <span className={styles.metaValue}>{camera.asset_id}</span>
                      </div>
                    </>
                  ) : (
                    <p className={styles.help}>Not bound to an edge agent — not monitored.</p>
                  )}
                  <div className={styles.metaRow}>
                    <span>Created</span>
                    <span className={styles.metaValue}>
                      {new Date(camera.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </aside>
            )}
          </div>
        )}
      </main>
      {clipOverlay && (
        // biome-ignore lint/a11y/useKeyWithClickEvents: Esc is handled on document
        <div
          className={styles.clipOverlayBackdrop}
          onClick={(e) => {
            if (e.target === e.currentTarget) setClipOverlay(null);
          }}
          role="presentation"
        >
          <dialog open className={styles.clipOverlayPanel} aria-label="Clip preview">
            <div className={styles.clipOverlayHeader}>
              <span className={styles.clipOverlayTitle}>{camera?.name}</span>
              <span className={styles.clipOverlayRange}>{clipOverlay.range}</span>
              <button
                type="button"
                className={styles.clipOverlayClose}
                onClick={() => setClipOverlay(null)}
                aria-label="Close preview"
              >
                ✕
              </button>
            </div>
            {/* biome-ignore lint/a11y/useMediaCaption: surveillance clips have no captions */}
            <video
              className={styles.clipOverlayVideo}
              src={clipOverlay.src}
              controls
              autoPlay
              playsInline
            />
          </dialog>
        </div>
      )}
    </Layout>
  );
};
